import React, { useState, useEffect } from 'react';
import { mockAuth, mockOrders, mockQuotes, subscribeToDB } from '../services/mockDatabase';
import { User, MapPin, Package, MessageSquare, CreditCard, Check } from 'lucide-react';

const Profile = ({ onAddToCart }) => {
  const [user, setUser] = useState(null);
  
  // Dashboard lists
  const [ordersList, setOrdersList] = useState([]);
  const [quotesList, setQuotesList] = useState([]);

  // Form states
  const [displayName, setDisplayName] = useState('');
  const [shippingAddress, setShippingAddress] = useState({
    line1: '',
    city: '',
    state: '',
    postal_code: ''
  });
  
  const [message, setMessage] = useState('');

  useEffect(() => {
    const currentUser = mockAuth.getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
      setDisplayName(currentUser.displayName);
      if (currentUser.shippingAddress) {
        setShippingAddress(currentUser.shippingAddress);
      }
      
      // Load orders & quotes
      setOrdersList(mockOrders.getOrders(currentUser.uid));
      setQuotesList(mockQuotes.getQuotes(currentUser.uid));
    }

    // Subscribe to DB changes
    const unsubscribe = subscribeToDB(() => {
      const u = mockAuth.getCurrentUser();
      if (u) {
        setUser(u);
        setOrdersList(mockOrders.getOrders(u.uid));
        setQuotesList(mockQuotes.getQuotes(u.uid));
      }
    });

    return unsubscribe;
  }, []);

  const handleUpdateProfile = (e) => {
    e.preventDefault();
    try {
      mockAuth.updateProfile(displayName, shippingAddress);
      setMessage('Profile details updated successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      alert(err.message);
    }
  };

  // Convert Quoted Custom Quote to Cart Item
  const handleBuyQuote = (quote) => {
    const quotedCartItem = {
      id: `quote_${quote.id}`,
      name: `Custom Order: ${quote.description.substring(0, 25)}...`,
      price: quote.quotedPrice,
      quantity: 1,
      customOptions: {
        letters: quote.customText,
        colorScheme: quote.customColors,
        notes: `Referenced quote ID: ${quote.id}`
      }
    };

    onAddToCart(quotedCartItem);
    
    // Mark quote as approved/purchased
    mockQuotes.updateQuoteStatus(quote.id, 'approved');
    
    // Redirect to cart
    window.location.hash = '#/cart';
  };

  if (!user) {
    return (
      <div className="text-center py-20 bg-white rounded-3xl shadow-sm max-w-md mx-auto my-12 border border-gray-100 p-6">
        <p className="text-gray-400 italic mb-4">Please log in to view your profile dashboard.</p>
        <a
          href="#/login"
          className="inline-block bg-pink-500 hover:bg-pink-600 text-white font-bold px-6 py-2.5 rounded-full shadow transition"
        >
          Sign In
        </a>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto my-12 px-6">
      <div className="flex flex-col md:flex-row gap-8">
        
        {/* Left Side: Profile Information & Address */}
        <div className="w-full md:w-80 bg-white p-6 rounded-3xl shadow-md border border-pink-50 h-fit space-y-6">
          <div className="text-center border-b border-gray-100 pb-5">
            <div className="w-20 h-20 bg-pink-100 text-pink-500 rounded-full flex items-center justify-center mx-auto mb-3 font-bold text-2xl">
              {user.displayName.substring(0, 1).toUpperCase()}
            </div>
            <h3 className="font-bold text-gray-800 text-lg leading-tight">{user.displayName}</h3>
            <span className="text-xs text-gray-400 font-bold uppercase">{user.role} Account</span>
          </div>

          {message && (
            <div className="text-center text-xs font-semibold text-green-700 bg-green-50 p-3 rounded-xl border border-green-200">
              {message}
            </div>
          )}

          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1 flex items-center gap-1">
                <User size={12} /> Full Name
              </label>
              <input
                type="text"
                required
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full p-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1 flex items-center gap-1">
                <MapPin size={12} /> Shipping Address
              </label>
              <div className="space-y-2">
                <input
                  type="text"
                  required
                  value={shippingAddress.line1}
                  onChange={(e) => setShippingAddress({ ...shippingAddress, line1: e.target.value })}
                  placeholder="123 Main St"
                  className="w-full p-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    required
                    value={shippingAddress.city}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                    placeholder="City"
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                  <input
                    type="text"
                    required
                    value={shippingAddress.state}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                    placeholder="State"
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                </div>
                <input
                  type="text"
                  required
                  value={shippingAddress.postal_code}
                  onChange={(e) => setShippingAddress({ ...shippingAddress, postal_code: e.target.value })}
                  placeholder="Zip / Postal"
                  className="w-full p-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-pink-500 hover:bg-pink-600 text-white font-bold py-2.5 rounded-xl transition text-sm"
            >
              Save Address Details
            </button>
          </form>
        </div>

        {/* Right Side: Orders History & Quotes Tracker */}
        <div className="flex-1 space-y-8">
          
          {/* Order History */}
          <div>
            <h3 className="font-bold text-gray-800 text-lg mb-4 flex items-center gap-2">
              <Package className="text-pink-500" /> Your Order History ({ordersList.length})
            </h3>
            {ordersList.length === 0 ? (
              <div className="text-center py-8 bg-white rounded-3xl border border-gray-100 text-gray-400 italic">You haven't placed any orders yet.</div>
            ) : (
              <div className="space-y-4">
                {ordersList.map(order => (
                  <div key={order.id} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-800 text-sm font-mono">{order.id}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                          order.status === 'shipped' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {order.status.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Ordered on: {new Date(order.createdAt).toLocaleDateString()}</p>
                      
                      <div className="text-xs text-gray-600 mt-2 space-y-0.5">
                        {order.items.map((item, idx) => (
                          <div key={idx}>
                            • {item.quantity}x {item.name} 
                            {item.customOptions && item.customOptions.letters && (
                              <span className="text-purple-600 font-bold ml-1">[{item.customOptions.letters.toUpperCase()}]</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="text-left sm:text-right shrink-0">
                      <span className="block text-[10px] text-gray-400 font-bold uppercase">Order Total</span>
                      <span className="text-pink-600 font-extrabold text-lg">${order.totalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Custom Quotes Tracker */}
          <div>
            <h3 className="font-bold text-gray-800 text-lg mb-4 flex items-center gap-2">
              <MessageSquare className="text-pink-500" /> Custom Designs & Quotes ({quotesList.length})
            </h3>
            {quotesList.length === 0 ? (
              <div className="text-center py-8 bg-white rounded-3xl border border-gray-100 text-gray-400 italic">No custom quote requests requested.</div>
            ) : (
              <div className="space-y-4">
                {quotesList.map(quote => (
                  <div key={quote.id} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-gray-800 text-sm font-mono">{quote.id}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          quote.status === 'approved' ? 'bg-green-100 text-green-700' :
                          quote.status === 'quoted' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {quote.status.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 font-semibold italic">"{quote.description}"</p>
                      
                      {quote.customText && (
                        <p className="text-[11px] text-purple-600 font-bold mt-1.5">Letter Beads: "{quote.customText.toUpperCase()}"</p>
                      )}
                    </div>

                    <div className="text-left sm:text-right shrink-0 space-y-2">
                      {quote.quotedPrice && (
                        <div>
                          <span className="block text-[10px] text-gray-400 font-bold uppercase">Quoted Price</span>
                          <span className="text-purple-600 font-extrabold text-base">${quote.quotedPrice.toFixed(2)}</span>
                        </div>
                      )}
                      
                      {quote.status === 'quoted' && (
                        <button
                          onClick={() => handleBuyQuote(quote)}
                          className="inline-flex items-center gap-1.5 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold py-2 px-4 rounded-xl text-xs shadow-md transition-transform active:scale-95"
                        >
                          <CreditCard size={14} /> Buy & Checkout
                        </button>
                      )}

                      {quote.status === 'approved' && (
                        <span className="text-xs text-green-600 font-bold flex items-center gap-1.5 justify-end">
                          <Check size={14} /> Added to Cart / Completed
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

      </div>
    </div>
  );
};

export default Profile;
